import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, Chip, CircularProgress, Snackbar,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton, Divider,
} from '@mui/material';
import {
  Refresh, Lock, LockOpen, CheckCircleOutline, WarningAmberRounded, ErrorOutline, ChevronRight,
  Send, GavelRounded, TrendingUp, TrendingDown, AccountBalance, ReceiptLong, Savings,
  EventAvailable, ArrowForward, InfoOutlined,
} from '@mui/icons-material';
import { RouterLink } from '../../utils/linkNav';
import { periodCloseService, PeriodRow, CloseSummary, ChecklistItem, PeriodException, Domain } from '../../services/periodCloseService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { StatCard, ErrorState } from '../../components/common/pageKit';

const FYS = ['FY2025-26', 'FY2026-27', 'FY2027-28'];

const STATUS_META: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'accent' | 'sub' }> = {
  OPEN: { label: 'Open', tone: 'good' },
  CLOSED: { label: 'Closed', tone: 'sub' },
  LOCKED: { label: 'Locked', tone: 'sub' },
  UPCOMING: { label: 'Upcoming', tone: 'sub' },
  PENDING_APPROVAL: { label: 'Pending Approval', tone: 'warn' },
  CLOSE_IN_PROGRESS: { label: 'In Progress', tone: 'warn' },
  REOPENED: { label: 'Reopened', tone: 'accent' },
};

const readinessTone = (pct: number, c: any) => (pct >= 90 ? c.good : pct >= 70 ? c.warn : c.bad);
const statusTint = (s: string, c: any) => c[STATUS_META[s]?.tone || 'sub'];
const chkTint = (s: string, c: any) => (s === 'PASS' ? c.good : s === 'FAIL' ? c.bad : c.warn);

const PeriodCloseControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [fy, setFy] = useState('FY2026-27');
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [selected, setSelected] = useState<PeriodRow | null>(null);
  const [summary, setSummary] = useState<CloseSummary | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [exceptions, setExceptions] = useState<PeriodException[]>([]);
  const [finSummary, setFinSummary] = useState<{ current: any; previous: any; deltas: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reopenFor, setReopenFor] = useState<PeriodRow | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const loadPeriods = useCallback(() => {
    setLoading(true);
    periodCloseService.listPeriods(fy)
      .then((r) => {
        const ps = r.periods || [];
        setPeriods(ps);
        // Default selection: the live OPEN period, else the last CLOSED, else first.
        const open = ps.find((p) => (p.status || (p.is_closed ? 'CLOSED' : 'OPEN')) === 'OPEN');
        const pick = open || [...ps].reverse().find((p) => p.is_closed) || ps[0] || null;
        setSelected((prev) => (prev && ps.some((p) => p.id === prev.id) ? prev : pick));
        setError(null);
      })
      .catch((e) => setError(e.message || 'Failed to load periods'))
      .finally(() => setLoading(false));
  }, [fy]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const loadDetail = useCallback((p: PeriodRow) => {
    setDetailLoading(true);
    Promise.all([
      periodCloseService.summary(p.financial_year, p.period_number),
      periodCloseService.checklist(p.financial_year, p.period_number),
      periodCloseService.exceptions(p.financial_year, p.period_number),
      periodCloseService.periodSummary(p.financial_year, p.period_number),
    ])
      .then(([s, cl, ex, fs]) => {
        setSummary(s); setChecklist(cl.items || []); setExceptions(ex.exceptions || []);
        setFinSummary({ current: fs.current, previous: fs.previous, deltas: fs.deltas });
        setError(null);
      })
      .catch((e) => setError(e.message || 'Failed to load period detail'))
      .finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const refreshAll = () => { loadPeriods(); if (selected) loadDetail(selected); };

  const status = selected ? (selected.status || (selected.is_closed ? 'CLOSED' : 'OPEN')) : 'OPEN';
  const isClosed = status === 'CLOSED' || status === 'LOCKED';
  const blockingCount = summary?.blockingCount ?? exceptions.filter((e) => e.blocking).length;

  const runAction = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true);
    try { await fn(); setToast(msg); setReviewOpen(false); setOverrideReason(''); refreshAll(); }
    catch (e: any) { setToast(e.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const doSubmit = () => selected && runAction(() => periodCloseService.submit(selected.financial_year, selected.period_number), 'Submitted for approval');
  const doClose = () => selected && runAction(
    () => periodCloseService.approve(selected.financial_year, selected.period_number, blockingCount > 0 ? { override: true, reason: overrideReason } : {}),
    'Period closed');
  const doReopen = () => reopenFor && runAction(
    () => periodCloseService.reopen(reopenFor.financial_year, reopenFor.period_number, reopenReason),
    'Period reopened').then(() => { setReopenFor(null); setReopenReason(''); });

  // ---- executive KPI cards ----
  const kpis = summary ? [
    { title: 'Unposted Journals', color: summary.unpostedTransactions ? '#dc2626' : '#059669', icon: <ReceiptLong />, value: summary.unpostedTransactions, route: '/finance/journal' },
    { title: 'Unreconciled Bank', color: summary.unreconciledBankItems ? '#d97706' : '#059669', icon: <AccountBalance />, value: summary.unreconciledBankItems, route: '/finance/bank-reconciliation' },
    { title: 'Open Receivables', color: '#0284c7', icon: <TrendingUp />, value: formatIndianNumber(summary.openAR), route: '/finance/ageing' },
    { title: 'Open Payables', color: '#7c3aed', icon: <TrendingDown />, value: formatIndianNumber(summary.openAP), route: '/finance/ageing' },
    { title: 'Tax Items Pending', color: summary.taxItemsPending ? '#d97706' : '#059669', icon: <GavelRounded />, value: summary.taxItemsPending, route: '/finance/tds-reports' },
    { title: 'Journal Exceptions', color: summary.journalsPending ? '#d97706' : '#059669', icon: <WarningAmberRounded />, value: summary.journalsPending, route: '/finance/journal' },
  ] : [];

  const readinessPct = summary?.closeReadiness ?? 0;

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Period Close Control Center</Typography>
          <Typography variant="body2" color="text.secondary">Month-end close readiness, blocking exceptions and financial snapshot — every figure computed from posted data.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField select size="small" label="Financial Year" value={fy} onChange={(e) => { setFy(e.target.value); setSelected(null); }} sx={{ width: 150 }}>
            {FYS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAll}>Refresh</Button>
        </Stack>
      </Stack>

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={refreshAll} /></Box>}

      {/* ---- Period timeline ---- */}
      <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <EventAvailable sx={{ fontSize: 18, color: c.accent }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Close Timeline — {fy}</Typography>
        </Stack>
        {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}><CircularProgress size={22} /></Box> : (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
            {periods.map((p) => {
              const st = p.status || (p.is_closed ? 'CLOSED' : 'OPEN');
              const tint = statusTint(st, c);
              const active = selected?.id === p.id;
              const pct = p.readiness_pct;
              return (
                <Box key={p.id} onClick={() => setSelected(p)}
                  sx={{ minWidth: 104, flex: '1 0 auto', cursor: 'pointer', borderRadius: 2, p: 1.25,
                    border: `1.5px solid ${active ? c.accent : c.line}`, bgcolor: active ? `${c.accent}0d` : 'transparent',
                    transition: 'all .15s', '&:hover': { borderColor: c.accent } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{p.period_name.slice(0, 3)}</Typography>
                    {st === 'CLOSED' || st === 'LOCKED' ? <Lock sx={{ fontSize: 13, color: tint }} />
                      : st === 'OPEN' || st === 'REOPENED' ? <LockOpen sx={{ fontSize: 13, color: tint }} /> : null}
                  </Stack>
                  <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: `${tint}22`, overflow: 'hidden' }}>
                    <Box sx={{ width: `${pct ?? 0}%`, height: '100%', bgcolor: readinessTone(pct ?? 0, c) }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: tint, fontWeight: 700, fontSize: '0.625rem' }}>{STATUS_META[st]?.label || st}</Typography>
                  {pct != null && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.625rem' }}>{pct}%</Typography>}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {!selected ? (
        !loading && <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>No periods for {fy}.</Typography>
      ) : (
        <>
          {/* ---- Executive close summary header ---- */}
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ position: 'relative', width: 92, height: 92, flexShrink: 0 }}>
                    <CircularProgress variant="determinate" value={100} size={92} thickness={4} sx={{ color: c.line, position: 'absolute' }} />
                    <CircularProgress variant="determinate" value={readinessPct} size={92} thickness={4} sx={{ color: readinessTone(readinessPct, c) }} />
                    <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: readinessTone(readinessPct, c) }}>{readinessPct}%</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Close Readiness</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{(summary?.currentPeriod as any)?.label || selected.period_name}</Typography>
                    <Chip size="small" label={STATUS_META[status]?.label || status}
                      sx={{ height: 22, fontWeight: 700, bgcolor: `${statusTint(status, c)}1f`, color: statusTint(status, c) }} />
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.5, borderRadius: 2, border: line, bgcolor: blockingCount > 0 ? `${c.bad}0d` : `${c.good}0d` }}>
                  {blockingCount > 0 ? <ErrorOutline sx={{ color: c.bad }} /> : <CheckCircleOutline sx={{ color: c.good }} />}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {blockingCount > 0 ? `${blockingCount} blocking issue${blockingCount === 1 ? '' : 's'} must be cleared` : isClosed ? 'This period is closed' : 'No blocking issues — ready to close'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exceptions.length} exception{exceptions.length === 1 ? '' : 's'} · {checklist.filter((i) => i.status === 'PASS').length}/{checklist.length} checks passing
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  {isClosed ? (
                    <Button variant="outlined" startIcon={<LockOpen />} disabled={busy} onClick={() => { setReopenFor(selected); setReopenReason(''); }}>Reopen</Button>
                  ) : (
                    <>
                      {status !== 'PENDING_APPROVAL' && <Button variant="outlined" startIcon={<Send />} disabled={busy || detailLoading} onClick={doSubmit}>Submit</Button>}
                      <Button variant="contained" startIcon={<Lock />} disabled={busy || detailLoading} onClick={() => setReviewOpen(true)}>Close Period</Button>
                    </>
                  )}
                  <Tooltip title="Open Period 360"><span><IconButton component={RouterLink} to={`/finance/period-close/${selected.financial_year}/${selected.period_number}`} sx={{ border: line }}><ArrowForward fontSize="small" /></IconButton></span></Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* ---- Executive KPI strip ---- */}
          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            {(summary ? kpis : Array.from({ length: 6 })).map((k: any, i) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
                {summary ? <StatCard title={k.title} value={k.value} icon={k.icon} color={k.color} onClick={() => navigate(k.route)} />
                  : <Paper elevation={0} sx={{ ...panelSx, height: 100, display: 'grid', placeItems: 'center' }}><CircularProgress size={18} /></Paper>}
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2.5}>
            {/* ---- Readiness breakdown ---- */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 2 }}>Readiness by Domain</Typography>
                <Stack spacing={1.5}>
                  {(summary?.domains || []).map((d: Domain) => {
                    const tint = chkTint(d.status, c);
                    return (
                      <Box key={d.key}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>{d.score}%</Typography>
                        </Stack>
                        <Box sx={{ height: 6, borderRadius: 3, bgcolor: `${tint}22`, overflow: 'hidden' }}>
                          <Box sx={{ width: `${d.score}%`, height: '100%', bgcolor: tint }} />
                        </Box>
                      </Box>
                    );
                  })}
                  {!summary && <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={20} /></Box>}
                </Stack>
              </Paper>
            </Grid>

            {/* ---- Close checklist ---- */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Close Checklist</Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Chip size="small" label={`${checklist.filter((i) => i.status === 'PASS').length} pass`} sx={{ height: 22, fontWeight: 700, bgcolor: `${c.good}1f`, color: c.good }} />
                    <Chip size="small" label={`${checklist.filter((i) => i.status === 'WARN').length} warn`} sx={{ height: 22, fontWeight: 700, bgcolor: `${c.warn}1f`, color: c.warn }} />
                    <Chip size="small" label={`${checklist.filter((i) => i.status === 'FAIL').length} fail`} sx={{ height: 22, fontWeight: 700, bgcolor: `${c.bad}1f`, color: c.bad }} />
                  </Stack>
                </Stack>
                <Stack spacing={0.75} sx={{ maxHeight: 380, overflowY: 'auto' }}>
                  {detailLoading && !checklist.length ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={20} /></Box>
                    : checklist.map((it, i) => {
                      const tint = chkTint(it.status, c);
                      return (
                        <Stack key={i} direction="row" alignItems="center" spacing={1.25} component={RouterLink} to={it.route}
                          sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: c.panel2 } }}>
                          {it.status === 'PASS' ? <CheckCircleOutline sx={{ fontSize: 18, color: tint }} />
                            : it.status === 'FAIL' ? <ErrorOutline sx={{ fontSize: 18, color: tint }} />
                              : <WarningAmberRounded sx={{ fontSize: 18, color: tint }} />}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.check}</Typography>
                              {it.blocking && <Chip size="small" label="BLOCKING" sx={{ height: 16, fontSize: '0.5625rem', fontWeight: 800, bgcolor: `${c.bad}1a`, color: c.bad }} />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.description}</Typography>
                          </Box>
                          {(it.count != null || it.amount != null) && (
                            <Typography variant="caption" sx={{ fontWeight: 700, color: tint, whiteSpace: 'nowrap' }}>
                              {it.count != null ? it.count : ''}{it.count != null && it.amount ? ' · ' : ''}{it.amount ? formatIndianNumber(it.amount) : ''}
                            </Typography>
                          )}
                          <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
                        </Stack>
                      );
                    })}
                </Stack>
              </Paper>
            </Grid>

            {/* ---- Exceptions ---- */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Exceptions</Typography>
                  <Chip size="small" label={`${exceptions.filter((e) => e.blocking).length} blocking`}
                    sx={{ height: 22, fontWeight: 700, bgcolor: exceptions.some((e) => e.blocking) ? `${c.bad}1f` : `${c.good}1f`, color: exceptions.some((e) => e.blocking) ? c.bad : c.good }} />
                </Stack>
                <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {exceptions.length === 0 ? (
                    <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                      <CheckCircleOutline sx={{ fontSize: 30, color: c.good }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No exceptions</Typography>
                    </Stack>
                  ) : exceptions.map((ex, i) => {
                    const tint = ex.severity === 'BLOCKING' ? c.bad : ex.severity === 'WARNING' ? c.warn : c.accent;
                    return (
                      <Stack key={i} direction="row" alignItems="center" spacing={1.25} component={RouterLink} to={ex.route}
                        sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: c.panel2 } }}>
                        {ex.severity === 'BLOCKING' ? <ErrorOutline sx={{ fontSize: 18, color: tint }} />
                          : ex.severity === 'WARNING' ? <WarningAmberRounded sx={{ fontSize: 18, color: tint }} />
                            : <InfoOutlined sx={{ fontSize: 18, color: tint }} />}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{ex.title}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.detail}</Typography>
                        </Box>
                        {!!ex.amount && <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>{formatIndianNumber(ex.amount)}</Typography>}
                        <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>

            {/* ---- Financial snapshot ---- */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Savings sx={{ fontSize: 18, color: c.accent }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Financial Snapshot</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">vs previous period</Typography>
                </Stack>
                {finSummary ? (
                  <Stack divider={<Divider sx={{ borderColor: c.line }} />} spacing={0}>
                    {[
                      { k: 'revenue', label: 'Revenue', good: 'up' },
                      { k: 'cogs', label: 'COGS', good: 'down' },
                      { k: 'grossProfit', label: 'Gross Profit', good: 'up' },
                      { k: 'opex', label: 'Operating Expenses', good: 'down' },
                      { k: 'netProfit', label: 'Net Profit', good: 'up' },
                      { k: 'ar', label: 'Receivables (closing)', good: null },
                      { k: 'ap', label: 'Payables (closing)', good: null },
                      { k: 'cash', label: 'Cash & Bank', good: 'up' },
                    ].map((row) => {
                      const val = finSummary.current?.[row.k] ?? 0;
                      const delta = finSummary.deltas?.[row.k];
                      const up = (delta ?? 0) >= 0;
                      const deltaGood = row.good == null ? null : row.good === 'up' ? up : !up;
                      const deltaColor = deltaGood == null ? c.sub : deltaGood ? c.good : c.bad;
                      return (
                        <Stack key={row.k} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(val)}</Typography>
                            {delta != null && (
                              <Stack direction="row" alignItems="center" sx={{ minWidth: 74, justifyContent: 'flex-end' }}>
                                {up ? <TrendingUp sx={{ fontSize: 14, color: deltaColor }} /> : <TrendingDown sx={{ fontSize: 14, color: deltaColor }} />}
                                <Typography variant="caption" sx={{ fontWeight: 700, color: deltaColor }}>{formatIndianNumber(Math.abs(delta))}</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={20} /></Box>}
                {finSummary && !finSummary.previous && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>No previous period — deltas unavailable.</Typography>}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* ---- Review / Close dialog ---- */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Close {(summary?.currentPeriod as any)?.label || selected?.period_name}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, border: line, flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: readinessTone(readinessPct, c) }}>{readinessPct}%</Typography>
              <Typography variant="caption" color="text.secondary">Readiness</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, border: line, flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: blockingCount ? c.bad : c.good }}>{blockingCount}</Typography>
              <Typography variant="caption" color="text.secondary">Blocking</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, border: line, flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{checklist.filter((i) => i.status === 'PASS').length}/{checklist.length}</Typography>
              <Typography variant="caption" color="text.secondary">Checks Pass</Typography>
            </Box>
          </Stack>
          {blockingCount > 0 ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <ErrorOutline sx={{ color: c.bad, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: c.bad }}>{blockingCount} blocking exception(s) — closing requires an override reason.</Typography>
              </Stack>
              {exceptions.filter((e) => e.blocking).map((e, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5 }}>• {e.title}</Typography>
              ))}
              <TextField fullWidth size="small" sx={{ mt: 2 }} label="Override reason (required)" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} multiline minRows={2} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">No blocking issues. Closing locks the period — posting a journal into a closed month will be rejected until it is reopened.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button variant="outlined" startIcon={<Send />} disabled={busy} onClick={doSubmit}>Submit for Approval</Button>
          <Button variant="contained" startIcon={<Lock />} disabled={busy || (blockingCount > 0 && !overrideReason.trim())} onClick={doClose}>
            {blockingCount > 0 ? 'Force Close' : 'Close Period'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Reopen dialog ---- */}
      <Dialog open={!!reopenFor} onClose={() => setReopenFor(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Reopen {reopenFor?.period_name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Reopening allows postings back into this month. A reason is recorded on the audit trail.</Typography>
          <TextField fullWidth size="small" autoFocus multiline minRows={2} label="Reason for reopening (required)" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenFor(null)}>Cancel</Button>
          <Button color="warning" variant="contained" startIcon={<LockOpen />} disabled={busy || !reopenReason.trim()} onClick={doReopen}>Reopen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default PeriodCloseControlCenter;
